import * as React from 'react'
import { applyMiddleware, combineReducers, createStore, Middleware } from 'redux'
import { registerActions as registerSegmentActions } from './segment/actions'
import { middleware as segmentMiddleware } from './segment/api'
import { composeWithDevTools } from 'redux-devtools-extension'
import { render } from 'react-dom'
import reduxLogger from 'redux-logger'
import reduxCatch from 'redux-catch'
import thunk from 'redux-thunk'

import { getPlatformTokenOrFail, checkProfile, profileNeedsReLoad, reducer as authReducer, selectors as authSelectors } from './lifecycle/auth'
import { actions as lifecycleActions, reducer as lifecycleReducer } from './lifecycle/lifecycle'
import { Action, Dispatch, ExtraArgs, GetState } from './types'
import thunkPromiseCatch from './util/thunkPromiseCatch'

import featuresCompatibilityFetchReducer from './common/compatibility/reducer'
import cloudHubSettingsReducer from './components/Settings/Body/CloudHub/reducer'
import hybridSettingsReducer from './components/Settings/Body/Hybrid/reducer'
import customMetricsReducer from './components/BuiltInDashboard/CustomMetrics/reducer'
import labelsFetchReducer from './common/labels/reducer'
import dashboardsReducer from './components/CustomDashboard/reducer'
import timePickerReducer from './components/TimePickerNavbar/reducer'
import emptyStateReducer from './components/EmptyStatePage/reducer'
import fullscreenReducer from './components/Fullscreen/reducer'
import dashboardReducer from './components/Dashboard/reducer'
import logPointsReducer from './components/LogPoints/LogPointsPage/reducer'
import resourceReducer from './components/ResourceSelector/reducer'
import settingsReducer from './components/Settings/reducer'
import reportsReducer from './components/Reports/reducer'
import builtinReducer from './components/BuiltInDashboard/reducer'
import rawDataReducer from './components/RawData/reducer'
import toastsReducer from './components/Toasts/reducer'
import iframeReducer from './components/Iframe/reducer'
import alertReducer from './components/Alerts/AlertsListPage/reducer'
import alertsReducer from './components/Alerts/reducer'
import basicAlertModalReducer from './components/Alerts/AlertModals/BasicAlertModal/reducer'
import advancedAlertModalReducer from './components/Alerts/AlertModals/AdvancedAlertModal/reducer'

import CoreServicesRemoteApi from './remote-api/CoreServicesRemoteApi'
import APIPlatformRemoteApi from './remote-api/APIPlatformRemoteApi'
import APIManagerRemoteApi from './remote-api/APIManagerRemoteApi'
import ArchiveAPIService from './remote-api/ArchiveAPIService'
import ARMRemoteApi from './remote-api/ARMRemoteApi'
import UIRemoteApi from './remote-api/UIRemoteApi'
import XAPIProxy from './remote-api/XAPIProxy'
import Root from './components/root/Root'

import './index.css'
import './util/ie11'

const rootReducer = {
  monitoring: combineReducers({
    auth: authReducer,
    featuresCompatibility: featuresCompatibilityFetchReducer('monitoring'),
    labels: labelsFetchReducer('monitoring'),
    cloudHubSettings: cloudHubSettingsReducer,
    hybridSettings: hybridSettingsReducer,
    customMetrics: customMetricsReducer,
    dashboards: dashboardsReducer,
    timePicker: timePickerReducer,
    emptyState: emptyStateReducer,
    fullscreen: fullscreenReducer,
    dashboard: dashboardReducer,
    lifecycle: lifecycleReducer,
    logPoints: logPointsReducer,
    resources: resourceReducer,
    settings: settingsReducer,
    builtIn: builtinReducer,
    rawData: rawDataReducer,
    reports: reportsReducer,
    toasts: toastsReducer,
    iframe: iframeReducer,
    alert: alertReducer,
    alerts: alertsReducer,
    basicAlertModal: basicAlertModalReducer,
    advancedAlertModal: advancedAlertModalReducer,
  })
};

const defaultErrorHandler = (error: any, getState: GetState, lastAction: Action, dispatch: Dispatch) => {
  console.error(error);
  dispatch(lifecycleActions.unexpectedError(error))
};

const errorHandler = (error: { errorCode: number, errorBody: any }, getState: GetState, lastAction: Action, dispatch: Dispatch) => {
  // checking 401 and 403 is ok. checking for 500 is a workaround for ui-api bug
  if ([401, 403].indexOf(error.errorCode) >= 0 || (error.errorCode === 500 && error.errorBody === 'Access denied. Details: Unauthorized')) {
    dispatch(profileNeedsReLoad())
  } else {
    defaultErrorHandler(error, getState, lastAction, dispatch)
  }
};

// create middleware
const thunkExtraArg: ExtraArgs = {
  api: {
    cs: (getState: GetState) => new CoreServicesRemoteApi(authSelectors.getAuthorization(getState())),
    arm: (getState: GetState) => new ARMRemoteApi(authSelectors.getAuthorization(getState())),
    apiManager: (getState: GetState) => new APIManagerRemoteApi(getPlatformTokenOrFail(getState())),
    xapiProxy: (getState: GetState) => new XAPIProxy(getPlatformTokenOrFail(getState())),
    apiPlatform: (getState: GetState) => new APIPlatformRemoteApi(authSelectors.getAuthorization(getState())),
    archiveAPI: (getState: GetState) => new ArchiveAPIService(authSelectors.getAuthorization(getState())),
    ui: (getState: GetState) => {
      const authorization = authSelectors.getAuthorization(getState())
      const maybeProfile = authSelectors.getProfile(getState())
      return new UIRemoteApi(authorization, maybeProfile)
    }
  }
};

const thunkMiddleware = thunk.withExtraArgument(thunkExtraArg);
const middlewares = [thunkPromiseCatch(errorHandler), reduxCatch(defaultErrorHandler), thunkMiddleware] as Middleware[];

if (window.location.search.indexOf('redux-logger=true') > -1) {
  middlewares.push(reduxLogger)
}

middlewares.push(segmentMiddleware);
registerSegmentActions();

const store = createStore(combineReducers(rootReducer), composeWithDevTools(applyMiddleware(...middlewares)));

store.dispatch(checkProfile());

render(
  <Root store={store}/>,
  document.getElementById('root') as HTMLElement
);
